import i18n from 'config/i18n';
import {
  Box,
  Text,
  Alert,
  Center,
  Spinner,
  Flex,
  HStack,
  SlideFade,
} from '@chakra-ui/react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { updateAdress, updateAdressDetails } from 'store/answers/answersSlice';
import config from 'config/config';
import autoCompleteInputStyle from 'ui/theme/autoCompleteInputStyle';
import { useEffect, useState } from 'react';
import { stringHasNumber } from 'utils/stringUtils';
import { getAddressDetails } from 'features/map/mapUtils';
import { AddressGeoCodeSelector, AddressSelector } from 'store/selectors';
import { useVendorConfig } from 'store/vendor/useVendorConfig';
import AppRoutes from 'config/AppRoutes';
import useAutoLocation from 'features/autoLocation/useAutoLocation';
import Button from 'ui/molecules/Button';
import { LocationIcon } from 'ui/icons/Icons';
import { isRosselVersion } from 'utils/vendorUtils';
import useStepNavigation from 'utils/hooks/useStepNavigation';
import { SimpleModal } from 'components/SimpleModal';
import mapCompressed from './map_compressed.jpg';

const PageAddress = () => {
  const { t } = useTranslation();
  const { acceptedCountries } = useVendorConfig();
  const currentRoute = AppRoutes.ADDRESS_CHOICE;

  // auto loc
  const [autoLocationEnabled, setAutoLocationEnabled] =
    useState<boolean>(false);
  const [showNoAddressWarning, setShowNoAddressWarning] =
    useState<boolean>(false);
  const {
    isLoading: autoLocationLoading,
    error: autoLocationError,
    currentLocation: autoLocationPosition,
    currentAddress: autoLocationAdressDetails,
  } = useAutoLocation(autoLocationEnabled);

  const dispatch = useDispatch();
  // HACK: this is used to refresh the adress input each time we change the adress to avoid weird behavior
  const [lastAdressChange, setLastAdressChange] = useState(0);
  const userAdress = useSelector(AddressSelector);
  const geoCode = useSelector(AddressGeoCodeSelector);
  const { navigateToNextStep } = useStepNavigation(currentRoute);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(userAdress);

  const isAdressValid = userAdress && userAdress === inputValue;
  const addressHasNumber = stringHasNumber(userAdress);

  // --------------------- methods ------------------------
  const moveToNextStep = () => {
    navigateToNextStep();
  };

  const handleContinue = () => {
    if (!addressHasNumber) {
      setShowNoAddressWarning(true);
      return;
    }
    moveToNextStep();
  };

  const handleGetCurrentLocation = () => {
    setAutoLocationEnabled(true);
  };

  const handleAddressChange = (value: any) => {
    const newAdress = value?.label;
    if (newAdress) {
      console.log(`handleAdressChange: ${newAdress}`);
      setInputValue(newAdress);
      setLastAdressChange(Date.now);
      if (userAdress !== newAdress) {
        setIsLoading(true);
        batch(() => {
          setAutoLocationEnabled(false);
          // dispatch(resetAll());
          dispatch(updateAdress(newAdress));
        });
      }
    }
  };

  // --------------------- effects ------------------------

  // retreive geocode from adress
  useEffect(() => {
    const fetchGeoCode = async () => {
      const newGeoCode = await getAddressDetails(userAdress);
      if (newGeoCode) {
        // eslint-disable-next-line no-console
        console.log(`retrieved address details:${JSON.stringify(newGeoCode)}`);
        setIsLoading(false);
        setInputValue(userAdress); // be sure that once adress is updated, we update input value!
        dispatch(updateAdressDetails(newGeoCode));
      }
    };
    if (userAdress) {
      setIsLoading(true);
      fetchGeoCode();
    }
  }, [userAdress, dispatch]);

  // update view based on auto location data
  useEffect(() => {
    if (
      autoLocationAdressDetails &&
      autoLocationPosition &&
      autoLocationAdressDetails.address
    ) {
      batch(() => {
        // setCurrentAdress(autoLocationAdressDetails.address);
        // dispatch(resetAll());
        autoLocationAdressDetails.address &&
          dispatch(updateAdress(autoLocationAdressDetails.address));
        dispatch(updateAdressDetails(autoLocationAdressDetails));
      });
    }
  }, [autoLocationAdressDetails, autoLocationPosition, dispatch]);

  // --------------------- render ------------------------

  return (
    <Flex
      height="100%"
      width="100%"
      justifyContent="center"
      alignItems="center"
      backgroundImage={`url(${mapCompressed})`}
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundBlendMode="darken"
      backgroundColor="blackAlpha.400"
    >
      <SlideFade
        in
        offsetX="0px"
        offsetY="-100px"
        style={{
          width: '100%',
          maxWidth: '600px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        transition={{ enter: { delay: 0.1, duration: 0.4 } }}
      >
        <Box
          bgColor="white"
          width={{ base: '90%', md: '700px' }}
          height="max-content"
          borderRadius="10px"
          justifyContent="center"
          px={{ base: '25px', md: '80px' }}
          py={{ base: '25px', md: '50px' }}
        >
          <HStack w="full" justifyContent="center" alignItems="center">
            <LocationIcon />
            <Text fontWeight="bold">{t('ADDRESS_CHOICE_subtitle')}</Text>
          </HStack>
          <Box width="full" marginTop="10px">
            {isRosselVersion && (
              <Text fontSize="sm" pb="2">
                {t('ADDRESS_CHOICE_subtitle.detail.rossel')}
              </Text>
            )}

            <GooglePlacesAutocomplete
              key={`${userAdress}_${lastAdressChange}`}
              apiKey={config.GOOGLE_API_KEY}
              autocompletionRequest={{
                componentRestrictions: {
                  country: acceptedCountries,
                },
                types: ['address'],
              }}
              apiOptions={{
                language: i18n.language,
                id: '__googleMapsScriptId',
                libraries: ['visualization', 'places', 'drawing'],
              }}
              // Check possible props of re-select :  https://react-select.com/props
              selectProps={{
                classNamePrefix: 'adress-select',
                styles: autoCompleteInputStyle,
                inputValue,
                loadingMessage: () => t('common.loading'),
                noOptionsMessage: () =>
                  isLoading
                    ? t('common.loading')
                    : t('ADDRESS_CHOICE_no_option'),
                defaultValue: inputValue,
                placeholder: userAdress || t('ADDRESS_CHOICE_placeholder'),

                textInputProps: {
                  autoFocus: true,
                },
                focused: true,

                onInputChange: (
                  value: any,
                  actionMeta: {
                    action: 'set-value' | 'input-change' | 'input-blur';
                    prevInputValue: string;
                  }
                ) => {
                  if (actionMeta.action === 'input-change') {
                    setInputValue(value);
                  } else if (
                    actionMeta.action === 'input-blur' &&
                    value !== userAdress
                  ) {
                    // set back previous address on blur
                    setInputValue(userAdress);
                  }
                },
                // shows menu only when there is an input different from current address
                menuIsOpen: !!inputValue && inputValue !== userAdress,
                onChange: handleAddressChange,
              }}
            />

            {config.options.adress.useAutoLocation &&
              !autoLocationAdressDetails?.address && (
                <Center pt="5px">
                  {autoLocationLoading ? (
                    <Spinner />
                  ) : (
                    <Button variant="skip" onClick={handleGetCurrentLocation}>
                      {t('ADDRESS_CHOICE_autoLocate')}
                    </Button>
                  )}
                </Center>
              )}

            {autoLocationEnabled && autoLocationError && (
              <Alert mt="2" status="error">
                {t('ADDRESS_CHOICE_autoLocate.error')}: {autoLocationError}
              </Alert>
            )}
          </Box>
          <Flex justifyContent="center" pt={{ base: '20px', md: '40px' }}>
            <Button
              w={{ base: 'full', md: 'auto' }}
              isDisabled={!isAdressValid || !geoCode}
              onClick={handleContinue}
              isLoading={autoLocationLoading || isLoading}
              _hover={{ bg: 'primaryColor' }}
            >
              {t('common.continue')}
            </Button>
          </Flex>
        </Box>
        <SimpleModal
          isOpen={showNoAddressWarning}
          description={t('ADDRESS_CHOICE_Error_noNumber')}
          onClose={() => setShowNoAddressWarning(false)}
          button={
            <Flex>
              <Button
                variant="backButton"
                onClick={() => setShowNoAddressWarning(false)}
              >
                {t('common.backButton')}
              </Button>
              <Button onClick={() => moveToNextStep()}>
                {t('common.continue')}
              </Button>
            </Flex>
          }
          modalContentProps={{
            maxWidth: { base: '90vw', md: '600px' },
          }}
        />
      </SlideFade>
    </Flex>
  );
};

export default PageAddress;
